import { useState, useEffect } from "react";
// @mui
import {
  Card,
  Table,
  Button,
  TableBody,
  Container,
  TableContainer,
} from "@mui/material";
// routes
import { PATH_DASHBOARD } from "../../../routes/paths";
// @types
import { IDirectDebit } from "../../../@types/directDebits";
// components
import Scrollbar from "../../../components/scrollbar";
import CustomBreadcrumbs from "../../../components/custom-breadcrumbs";
import { useSettingsContext } from "../../../components/settings";
import {
  useTable,
  getComparator,
  emptyRows,
  TableNoData,
  TableEmptyRows,
  TableHeadCustom,
} from "../../../components/table";
import SetupMandateDialog from "../../../components/setup-mandate-dialog";
// sections
import DirectDebitsTableToolbar from "../../../sections/@dashboard/directDebits/DirectDebitsTableToolbar";
import DirectDebitsTableRow from "../../../sections/@dashboard/directDebits/DirectDebitsTableRow";
// services
import { DirectDebitsService } from "../../../api/directDebits.service";
// hooks
import { useSnackbar } from "../../../components/snackbar";
import { useAuthContext } from "../../../auth/useAuthContext";
// utils
import { Helmet } from "react-helmet-async";

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: "clientName", label: "Client Name", align: "center" },
  { id: "clientId", label: "Client ID", align: "center" },
  { id: "directDebitStatus", label: "Direct Debit Status", align: "center" },
  { id: "paymentAmount", label: "Payment Amount", align: "center" },
  { id: "nextPaymentDueDate", label: "Next Payment Due Date", align: "center" },
  { id: "paymentStatus", label: "Payment Status", align: "center" },
  { id: "actions", label: "Action", align: "center" },
];

// ----------------------------------------------------------------------

export default function DirectDebitsListPage() {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    //
    onSort,
  } = useTable();

  const { themeStretch } = useSettingsContext();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuthContext();

  const [tableData, setTableData] = useState<IDirectDebit[]>([]);
  const [originalTableData, setOriginalTableData] = useState<any[]>([]); // Store original backend data

  const [filterName, setFilterName] = useState("");
  const [filterStatus, setFilterStatus] = useState<string[]>([]);
  const [openMandateDialog, setOpenMandateDialog] = useState(false);
  const [editingDirectDebit, setEditingDirectDebit] = useState<any>(null); // Store the original data for editing

  const directDebitsService = new DirectDebitsService();

  useEffect(() => {
    fetchDirectDebits();
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const shouldReopen = searchParams.get("reopen");
    const step = searchParams.get("step");
    const directDebitId = searchParams.get("ddId");
    const redirectFlowId = searchParams.get("flowId");

    if (shouldReopen === "true" && directDebitId && redirectFlowId) {
      // Store in session storage for the dialog to use
      sessionStorage.setItem("directDebitId", directDebitId);
      sessionStorage.setItem("redirectFlowId", redirectFlowId);
      sessionStorage.setItem("mandateDialogStep", step || "2");
      sessionStorage.setItem("reopenMandateDialog", "true");

      // Open the dialog
      setOpenMandateDialog(true);
    }
  }, []);

  const fetchDirectDebits = async () => {
    try {
      const response = await directDebitsService.getAllDirectDebits();
      const data = response.data;

      // Store original data for editing purposes
      setOriginalTableData(data);

      // 🔄 Map backend data to match frontend table structure
      const mappedData: IDirectDebit[] = data.map((dd: any) => ({
        id: dd.id,
        clientName:
          dd.patient?.firstName + " " + dd.patient?.surName || "Unknown",
        clientId: String(dd.patient?.id || "-"),
        directDebitStatus: dd.isAuthorized ? "Active" : "Pending",
        paymentAmount: dd.paymentAmount ?? 0,
        nextPaymentDueDate: dd.startDate, // adjust if backend sends schedule info
        paymentStatus: "On track", // TODO: map properly when backend adds status
      }));

      setTableData(mappedData);
    } catch (error) {
      console.error("Error fetching direct debits:", error);
      enqueueSnackbar("Failed to fetch direct debits", { variant: "error" });
    }
  };

  const handleDeleteRow = (id: number) => {
    const deleteRow = tableData.filter((row) => row.id !== id);
    setTableData(deleteRow);

    // Also remove from original data
    const deleteOriginal = originalTableData.filter((row) => row.id !== id);
    setOriginalTableData(deleteOriginal);

    if (page > 0) {
      if (dataInPage.length < 2) {
        setPage(page - 1);
      }
    }
  };

  const handleDeleteDirectDebit = async (id: number) => {
    try {
      if (!user?.isDemo) {
        await directDebitsService.deleteDirectDebit(id);
      }
      handleDeleteRow(id);
      enqueueSnackbar("Direct debit deleted successfully!", {
        variant: "success",
      });
    } catch (error) {
      console.error("Error deleting direct debit:", error);
      enqueueSnackbar("Failed to delete direct debit", { variant: "error" });
    }
  };

  const handleEditRow = (id: number) => {
    // Find the original data for this direct debit
    const originalDirectDebit = originalTableData.find((dd) => dd.id === id);

    if (originalDirectDebit) {
      // Prepare the data for the dialog
      const editData = {
        id: originalDirectDebit.id,
        status: originalDirectDebit.status, // ← ADD
        mandateReference: originalDirectDebit.mandateReference, // ← ADD
        accountLast4: originalDirectDebit.accountLast4, // ← ADD
        bankName: originalDirectDebit.bankName,
        patientId: originalDirectDebit.patient?.id,
        fullName: originalDirectDebit.patient
          ? `${originalDirectDebit.patient.firstName || ""} ${
              originalDirectDebit.patient.surName || ""
            } ${originalDirectDebit.patient.lastName || ""}`.trim()
          : "",
        address: originalDirectDebit.patient?.postCode || "",
        contactDetails: originalDirectDebit.patient
          ? `${originalDirectDebit.patient.email || ""} ${
              originalDirectDebit.patient.phone || ""
            }`.trim()
          : "",
        sortCode: originalDirectDebit.sortCode || "",
        accountNumber: originalDirectDebit.accountNumber || "",
        paymentFrequency: originalDirectDebit.paymentFrequency || "",
        paymentAmountType: originalDirectDebit.paymentAmountType || "",
        paymentAmount: originalDirectDebit.paymentAmount || null,
        startDate: originalDirectDebit.startDate || null,
        paymentReference: originalDirectDebit.paymentReference || "",
        understandsMandate: originalDirectDebit.understandsMandate || false,
        acknowledgesGuarantee:
          originalDirectDebit.acknowledgesGuarantee || false,
        willProvideConfirmation:
          originalDirectDebit.willProvideConfirmation || false,
        confirmsAccuracy: originalDirectDebit.confirmsAccuracy || false,
        mode: "edit",
      };

      setEditingDirectDebit(editData);
      setOpenMandateDialog(true);
    } else {
      enqueueSnackbar("Direct debit data not found", { variant: "error" });
    }
  };

  const handleViewRow = (id: number) => {
    // Find the original data for this direct debit
    const originalDirectDebit = originalTableData.find((dd) => dd.id === id);

    if (originalDirectDebit) {
      // Prepare the data for the dialog
      const editData = {
        id: originalDirectDebit.id,
        status: originalDirectDebit.status, // ← ADD
        mandateReference: originalDirectDebit.mandateReference, // ← ADD
        accountLast4: originalDirectDebit.accountLast4, // ← ADD
        bankName: originalDirectDebit.bankName,
        patientId: originalDirectDebit.patient?.id,
        fullName: originalDirectDebit.patient
          ? `${originalDirectDebit.patient.firstName || ""} ${
              originalDirectDebit.patient.surName || ""
            } ${originalDirectDebit.patient.lastName || ""}`.trim()
          : "",
        address: originalDirectDebit.patient?.postCode || "",
        contactDetails: originalDirectDebit.patient
          ? `${originalDirectDebit.patient.email || ""} ${
              originalDirectDebit.patient.phone || ""
            }`.trim()
          : "",
        sortCode: originalDirectDebit.sortCode || "",
        accountNumber: originalDirectDebit.accountNumber || "",
        paymentFrequency: originalDirectDebit.paymentFrequency || "",
        paymentAmountType: originalDirectDebit.paymentAmountType || "",
        paymentAmount: originalDirectDebit.paymentAmount || null,
        startDate: originalDirectDebit.startDate || null,
        paymentReference: originalDirectDebit.paymentReference || "",
        understandsMandate: originalDirectDebit.understandsMandate || false,
        acknowledgesGuarantee:
          originalDirectDebit.acknowledgesGuarantee || false,
        willProvideConfirmation:
          originalDirectDebit.willProvideConfirmation || false,
        confirmsAccuracy: originalDirectDebit.confirmsAccuracy || false,
        mode: "view",
      };

      setEditingDirectDebit(editData);
      setOpenMandateDialog(true);
    } else {
      enqueueSnackbar("Direct debit data not found", { variant: "error" });
    }
  };

  const handleCloseDialog = () => {
    setOpenMandateDialog(false);
    setEditingDirectDebit(null);
    // Refresh the data after dialog closes to show any updates
    fetchDirectDebits();
  };

  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const handleFilterStatus = (value: string[]) => {
    setPage(0);
    setFilterStatus(value);
  };

  const handleResetFilter = () => {
    setFilterName("");
    setFilterStatus([]);
  };

  const dataFiltered = applyFilter({
    inputData: tableData,
    comparator: getComparator(order, orderBy),
    filterName,
    filterStatus,
  });

  const dataInPage = dataFiltered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const denseHeight = dense ? 52 : 72;

  const isFiltered = filterName !== "" || filterStatus.length > 0;

  const isNotFound =
    (!dataFiltered.length && !!filterName) ||
    (!dataFiltered.length && !!filterStatus.length);

  return (
    <>
      <Helmet>
        <title>Direct Debits | OptoPro</title>
      </Helmet>

      <Container maxWidth={themeStretch ? false : "lg"}>
        <CustomBreadcrumbs
          heading="Direct Debits"
          links={[
            { name: "Dashboard", href: PATH_DASHBOARD.root },
            { name: "Direct Debits", href: PATH_DASHBOARD.root },
            { name: "List" },
          ]}
          action={
            !user?.isDemo && (
              <Button
                variant="contained"
                onClick={() => {
                  setEditingDirectDebit(null); // Ensure we're creating, not editing
                  setOpenMandateDialog(true);
                }}>
                Set up Mandate
              </Button>
            )
          }
        />

        <Card>
          <DirectDebitsTableToolbar
            isFiltered={isFiltered}
            filterName={filterName}
            filterStatus={filterStatus}
            onFilterName={handleFilterName}
            onFilterStatus={handleFilterStatus}
            onResetFilter={handleResetFilter}
          />

          <TableContainer sx={{ position: "relative", overflow: "unset" }}>
            <Scrollbar>
              <Table size={dense ? "small" : "medium"} sx={{ minWidth: 800 }}>
                <TableHeadCustom
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={tableData.length}
                  numSelected={0}
                  onSort={onSort}
                />

                <TableBody>
                  {dataFiltered
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                      <DirectDebitsTableRow
                        key={row.id}
                        row={row}
                        onEditRow={() => handleEditRow(row.id)}
                        onViewRow={() => handleViewRow(row.id)} // ← Add this
                        onDeleteRow={() => handleDeleteDirectDebit(row.id)}
                      />
                    ))}

                  <TableEmptyRows
                    height={denseHeight}
                    emptyRows={emptyRows(page, rowsPerPage, tableData.length)}
                  />

                  <TableNoData isNotFound={!dataFiltered.length} />
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>
        </Card>
      </Container>

      <SetupMandateDialog
        open={openMandateDialog}
        onClose={handleCloseDialog}
        initialData={editingDirectDebit} // Pass the editing data
      />
    </>
  );
}

// ----------------------------------------------------------------------

function applyFilter({
  inputData,
  comparator,
  filterName,
  filterStatus,
}: {
  inputData: IDirectDebit[];
  comparator: (a: any, b: any) => number;
  filterName: string;
  filterStatus: string[];
}) {
  const stabilizedThis = inputData.map((el, index) => [el, index] as const);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  inputData = stabilizedThis.map((el) => el[0]);

  if (filterName) {
    inputData = inputData.filter(
      (directDebit) =>
        directDebit.clientName
          .toLowerCase()
          .indexOf(filterName.toLowerCase()) !== -1 ||
        directDebit.clientId.toLowerCase().indexOf(filterName.toLowerCase()) !==
          -1,
    );
  }

  if (filterStatus.length) {
    inputData = inputData.filter((directDebit) =>
      filterStatus.includes(directDebit.directDebitStatus),
    );
  }

  return inputData;
}
